import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, ChevronLeft, ChevronRight, Plus, Send, Check, X, Users, Clock, Pencil, Trash2, User as UserIcon, Infinity } from "lucide-react";
import { toast } from "sonner";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SESSION_TYPES = ["OPEN_GYM", "STRENGTH", "MMA", "HYROX", "PT", "SEMI_PT"];
const DURATIONS = [20, 30, 45, 60, 90];
const TYPE_COLOR = {
  OPEN_GYM: "#A78BFA",
  STRENGTH: "#CCFF00",
  CONDITIONING: "#F97316",
  MMA: "#FF3B30",
  HYROX: "#FBBF24",
  PT: "#7DD3FC",
  SEMI_PT: "#22D3EE",
};

const TYPE_LABEL = {
  OPEN_GYM: "Open Gym",
  STRENGTH: "Strength",
  CONDITIONING: "Conditioning",
  MMA: "MMA",
  HYROX: "HYROX",
  PT: "Personal Training",
  SEMI_PT: "Semi-PT",
};

function _fmt12(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2,"0")} ${ampm}`;
}

function ClassTickerBanners({ branchId }) {
  const enabled = !!branchId;
  const stale = 5 * 60 * 1000;
  const fetchSlots = async (st) => (await apiClient.get(`/public/time-slots?branch_id=${branchId}&session_type=${st}`)).data;
  const qStrength = useQuery({ queryKey: ["slots", branchId, "STRENGTH"], queryFn: () => fetchSlots("STRENGTH"), enabled, staleTime: stale });
  const qCond = useQuery({ queryKey: ["slots", branchId, "CONDITIONING"], queryFn: () => fetchSlots("CONDITIONING"), enabled, staleTime: stale });
  const qMma = useQuery({ queryKey: ["slots", branchId, "MMA"], queryFn: () => fetchSlots("MMA"), enabled, staleTime: stale });
  const qHyrox = useQuery({ queryKey: ["slots", branchId, "HYROX"], queryFn: () => fetchSlots("HYROX"), enabled, staleTime: stale });
  const qOpen = useQuery({ queryKey: ["slots", branchId, "OPEN_GYM"], queryFn: () => fetchSlots("OPEN_GYM"), enabled, staleTime: stale });

  const byType = {
    STRENGTH: qStrength.data || [],
    CONDITIONING: qCond.data || [],
    MMA: qMma.data || [],
    HYROX: qHyrox.data || [],
    OPEN_GYM: qOpen.data || [],
  };
  const ORDER = ["STRENGTH", "CONDITIONING", "MMA", "HYROX", "OPEN_GYM"];

  const cards = ORDER.map((st) => {
    const slots = byType[st] || [];
    if (slots.length === 0) return null;
    if (st === "OPEN_GYM") {
      const s = slots[0];
      return { st, patternLines: [{ pattern: "Every day · walk-in anytime", times: `${_fmt12(s.start)}-${_fmt12(s.end)}` }] };
    }
    const byPat = {};
    for (const s of slots) {
      const p = s.day_pattern || "";
      (byPat[p] = byPat[p] || []).push(_fmt12(s.start));
    }
    const patternLines = Object.entries(byPat).map(([pattern, times]) => ({
      pattern, times: times.join(", "),
    }));
    return { st, patternLines };
  }).filter(Boolean);

  if (cards.length === 0) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 mb-3" data-testid={`class-tickers-${branchId}`}>
      {cards.map(({ st, patternLines }) => (
        <div
          key={st}
          className="bg-zinc-950 border rounded-sm px-3 py-2"
          style={{ borderColor: `${TYPE_COLOR[st]}40` }}
          data-testid={`ticker-${st}-${branchId}`}
        >
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: TYPE_COLOR[st] }} />
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: TYPE_COLOR[st] }}>
              {TYPE_LABEL[st]}
            </div>
          </div>
          {patternLines.map((line) => (
            <div key={line.pattern} className="text-[11px] text-zinc-300 leading-relaxed">
              <span className="text-zinc-500">{line.pattern}:</span> {line.times}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function OpenGymBanner({ branchId }) {
  const { data: slots = [] } = useQuery({
    queryKey: ["open-gym-slots", branchId],
    queryFn: async () => (await apiClient.get(`/public/time-slots?branch_id=${branchId}&session_type=OPEN_GYM`)).data,
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000,
  });
  const slot = slots[0];
  if (!slot) return null;
  const hours = `${slot.start ? slot.start.replace(":00","") : ""} - ${slot.end ? slot.end.replace(":00","") : ""}`;
  return (
    <div
      data-testid={`open-gym-banner-${branchId}`}
      className="flex items-center gap-3 bg-violet-500/10 border border-violet-500/30 rounded-sm px-3 py-2 mb-3"
    >
      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: TYPE_COLOR.OPEN_GYM }} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-300">Open Gym · Flexible walk-in</div>
        <div className="text-xs text-zinc-300 mt-0.5">
          {slot.label.replace("Flexible · Open Gym · ", "")} · every day (no batches — members walk in anytime)
        </div>
      </div>
      <Infinity size={16} className="text-violet-300 shrink-0" />
    </div>
  );
}


function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

/* ============================================================
   SUPER ADMIN — daily view + coach reassignment + approvals
   ============================================================ */
function SuperAdminScheduler() {
  const [dateObj, setDateObj] = useState(new Date());
  const [branchFilter, setBranchFilter] = useState("all");
  const [viewMode, setViewMode] = useState("daily"); // 'daily' | 'weekly'
  const dateStr = isoDate(dateObj);
  const qc = useQueryClient();

  // For weekly view: 7-day window starting Monday of dateObj's week
  const weekStart = useMemo(() => {
    const d = new Date(dateObj);
    const dow = (d.getDay() + 6) % 7; // 0 = Monday
    d.setDate(d.getDate() - dow);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [dateObj]);
  const weekDates = useMemo(() =>
    Array.from({ length: 6 }, (_, i) => {
      // Only Mon–Sat. Sunday is hidden — the gym is closed.
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    }), [weekStart]);
  const weekEnd = weekDates[5];

  const { data: branches = [] } = useQuery({
    queryKey: ["branches"],
    queryFn: async () => (await apiClient.get("/branches")).data,
  });

  const { data: dayData } = useQuery({
    queryKey: ["schedule-day", dateStr, branchFilter],
    queryFn: async () => {
      const params = { day: dateStr };
      if (branchFilter !== "all") params.branch_id = branchFilter;
      return (await apiClient.get("/schedule/day", { params })).data;
    },
    enabled: viewMode === "daily",
  });

  const { data: weekDayResults = [] } = useQuery({
    queryKey: ["schedule-week", isoDate(weekStart), branchFilter],
    queryFn: async () => {
      const results = await Promise.all(weekDates.map(async (d) => {
        const params = { day: isoDate(d) };
        if (branchFilter !== "all") params.branch_id = branchFilter;
        const r = await apiClient.get("/schedule/day", { params });
        return { date: isoDate(d), sessions: r.data?.sessions || [] };
      }));
      return results;
    },
    enabled: viewMode === "weekly",
  });

  const { data: coaches = [] } = useQuery({
    queryKey: ["coaches-all"],
    queryFn: async () => (await apiClient.get("/coaches")).data,
  });

  const { data: requests = [] } = useQuery({
    queryKey: ["change-requests-pending"],
    queryFn: async () => (await apiClient.get("/session-change-requests", { params: { status: "PENDING" } })).data,
  });

  const reassign = useMutation({
    mutationFn: async ({ sessionId, coachId }) => (await apiClient.patch(`/sessions/${sessionId}`, { coach_id: coachId })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule-day"] });
      toast.success("Coach reassigned");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const approve = useMutation({
    mutationFn: async (id) => (await apiClient.patch(`/session-change-requests/${id}/approve`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule-day"] });
      qc.invalidateQueries({ queryKey: ["change-requests-pending"] });
      toast.success("Approved & applied");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const reject = useMutation({
    mutationFn: async (id) => (await apiClient.patch(`/session-change-requests/${id}/reject`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["change-requests-pending"] });
      toast.success("Rejected");
    },
  });

  const sessions = dayData?.sessions || [];
  const grouped = useMemo(() => {
    const m = {};
    for (const s of sessions) {
      m[s.branch_id] = m[s.branch_id] || [];
      m[s.branch_id].push(s);
    }
    return m;
  }, [sessions]);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || id;

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="super-admin-scheduler">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Coach Hub · Timetable</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">
            {viewMode === "daily" ? "Daily Schedule" : "Weekly Schedule"}
          </h1>
          <div className="text-zinc-500 text-sm mt-1">Assign coaches to every session · approve Branch Admin requests</div>
        </div>
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden mr-2" data-testid="view-toggle">
            <button
              onClick={() => setViewMode("daily")}
              data-testid="view-daily"
              className={`px-3 py-2 text-xs uppercase tracking-[0.14em] transition-colors ${viewMode === "daily" ? "bg-volt text-black font-bold" : "text-zinc-400 hover:text-white"}`}>
              Daily
            </button>
            <button
              onClick={() => setViewMode("weekly")}
              data-testid="view-weekly"
              className={`px-3 py-2 text-xs uppercase tracking-[0.14em] transition-colors ${viewMode === "weekly" ? "bg-volt text-black font-bold" : "text-zinc-400 hover:text-white"}`}>
              Weekly
            </button>
          </div>
          <Button variant="ghost" onClick={() => setDateObj(new Date(dateObj.getTime() - (viewMode === "weekly" ? 7 : 1) * 86400000))} data-testid="date-prev">
            <ChevronLeft size={16} />
          </Button>
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm px-4 py-2 flex items-center gap-2" data-testid="date-display">
            <Calendar size={14} className="text-volt" />
            {viewMode === "daily" ? (
              <input type="date" value={dateStr} onChange={(e) => setDateObj(new Date(e.target.value))}
                className="bg-transparent text-white text-sm outline-none font-mono" data-testid="date-input" />
            ) : (
              <div className="text-white text-sm font-mono">
                {fmtDate(isoDate(weekStart))} → {fmtDate(isoDate(weekEnd))}
              </div>
            )}
          </div>
          <Button variant="ghost" onClick={() => setDateObj(new Date(dateObj.getTime() + (viewMode === "weekly" ? 7 : 1) * 86400000))} data-testid="date-next">
            <ChevronRight size={16} />
          </Button>
          <Button variant="outline" onClick={() => setDateObj(new Date())} data-testid="today-button" className="rounded-sm">Today</Button>
        </div>
      </div>

      {/* Branch filter */}
      <div className="flex items-center gap-3">
        <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Branch</Label>
        <Select value={branchFilter} onValueChange={setBranchFilter}>
          <SelectTrigger className="w-64 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="branch-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All branches</SelectItem>
            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Change request inbox */}
      {requests.length > 0 && (
        <Panel>
          <PanelHeader
            title={`🔴 ${requests.length} Pending Change Request${requests.length > 1 ? "s" : ""}`}
            subtitle="Branch Admin proposals awaiting your review"
          />
          <div className="space-y-2" data-testid="change-request-list">
            {requests.map((r) => (
              <div key={r.id} className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 flex items-center justify-between gap-3" data-testid={`change-request-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-[0.14em] text-zinc-500">
                    {r.requested_by_name} · {r.action} {r.target_type}
                  </div>
                  <div className="text-sm font-semibold mt-0.5 truncate">
                    {branchName(r.branch_id)} · {r.reason || "(no reason given)"}
                  </div>
                  <pre className="text-[10px] text-zinc-500 mt-1 font-mono truncate">{JSON.stringify(r.payload)}</pre>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" onClick={() => approve.mutate(r.id)} data-testid={`approve-${r.id}`}
                    className="bg-volt hover:bg-volt-hover text-black rounded-sm"><Check size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => reject.mutate(r.id)} data-testid={`reject-${r.id}`} className="rounded-sm">
                    <X size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Sessions by branch (daily view) */}
      {viewMode === "daily" && dateObj.getDay() === 0 && (
        <Panel>
          <div className="p-8 text-center space-y-2" data-testid="sunday-closed-state">
            <div className="text-4xl">🏝️</div>
            <div className="font-display text-2xl uppercase tracking-tight">Gym Closed on Sundays</div>
            <div className="text-zinc-500 text-sm">All branches are closed. Pick another day to view its schedule.</div>
          </div>
        </Panel>
      )}
      {viewMode === "daily" && dateObj.getDay() !== 0 && Object.keys(grouped).length === 0 && (
        <Panel><div className="text-zinc-500 text-sm p-4">No sessions scheduled on {fmtDate(dateStr)}.</div></Panel>
      )}
      {viewMode === "daily" && dateObj.getDay() !== 0 && Object.entries(grouped).map(([bId, list]) => (
        <Panel key={bId}>
          <PanelHeader title={branchName(bId)} subtitle={`${list.length} sessions · ${fmtDate(dateStr)}`} />
          <ClassTickerBanners branchId={bId} />
          <DailyHourTimeline
            sessions={list}
            coaches={coaches.filter((c) => (c.branch_ids || []).includes(bId))}
            onReassign={(sid, cid) => reassign.mutate({ sessionId: sid, coachId: cid })}
          />
        </Panel>
      ))}

      {/* Weekly calendar view */}
      {viewMode === "weekly" && (
        <WeeklyCalendarView
          weekDates={weekDates}
          weekDayResults={weekDayResults}
          branches={branches}
          branchFilter={branchFilter}
          onSelectDay={(d) => { setDateObj(new Date(d)); setViewMode("daily"); }}
        />
      )}
    </div>
  );
}

function DailyHourTimeline({ sessions, coaches, onReassign }) {
  // Google-Calendar-like hour grid, 5 AM → 11 PM
  const HOUR_START = 5;
  const HOUR_END = 23;
  const HOURS = Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i);
  const PX_PER_HOUR = 56;

  const items = sessions.map((s) => {
    const [d, t] = (s.start_time || "").split("T");
    const [h, m] = (t || "00:00").split(":").map(Number);
    const startMin = h * 60 + m;
    return {
      ...s,
      startMin,
      endMin: startMin + (s.duration_min || 60),
    };
  }).sort((a, b) => a.startMin - b.startMin);

  // Assign column indices to overlapping sessions (simple greedy)
  const columns = [];
  const positioned = items.map((s) => {
    let col = 0;
    while (columns[col] && columns[col].endMin > s.startMin) col++;
    columns[col] = s;
    return { ...s, col };
  });
  const maxCol = Math.max(0, ...positioned.map((p) => p.col)) + 1;

  return (
    <div className="relative" data-testid="daily-timeline">
      <div className="relative border border-zinc-900 rounded-sm" style={{ height: HOURS.length * PX_PER_HOUR }}>
        {HOURS.map((h, i) => (
          <div key={h} className="absolute left-0 right-0 border-t border-zinc-900 flex" style={{ top: i * PX_PER_HOUR }}>
            <div className="w-16 shrink-0 text-[10px] text-zinc-600 font-mono px-2 pt-1 border-r border-zinc-900">
              {h === 12 ? "12 PM" : h > 12 ? `${h - 12} PM` : `${h} AM`}
            </div>
          </div>
        ))}
        <div className="absolute left-16 right-2 top-0 bottom-0">
          {positioned.map((s) => {
            const top = ((s.startMin - HOUR_START * 60) / 60) * PX_PER_HOUR;
            const height = ((s.endMin - s.startMin) / 60) * PX_PER_HOUR - 2;
            const width = `calc(${100 / maxCol}% - 4px)`;
            const left = `calc(${(100 / maxCol) * s.col}% + 2px)`;
            const color = TYPE_COLOR[s.session_type] || "#3f3f46";
            return (
              <div
                key={s.id}
                data-testid={`session-block-${s.id}`}
                className="absolute rounded-sm p-2 overflow-hidden text-white"
                style={{ top, height, width, left, backgroundColor: `${color}30`, borderLeft: `3px solid ${color}` }}
              >
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color }}>
                  {s.session_type}
                </div>
                <div className="text-xs font-semibold truncate">{s.title}</div>
                <div className="text-[10px] text-zinc-400 mt-0.5">
                  {String(Math.floor(s.startMin / 60)).padStart(2, "0")}:{String(s.startMin % 60).padStart(2, "0")} · {s.duration_min}m
                </div>
                <select
                  value={s.coach_id || ""}
                  onChange={(e) => onReassign(s.id, e.target.value || null)}
                  className="mt-1 bg-zinc-950/80 border border-zinc-800 text-white text-[10px] rounded-sm w-full px-1 py-0.5"
                  data-testid={`session-coach-${s.id}`}
                >
                  <option value="">— Unassigned —</option>
                  {coaches.map((c) => (
                    <option key={c.id} value={c.id}>{c.full_name || c.name}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function WeeklyCalendarView({ weekDates, weekDayResults, branches, branchFilter, onSelectDay }) {
  const visibleBranches = branchFilter === "all"
    ? branches
    : branches.filter((b) => b.id === branchFilter);
  const dayName = (d) => ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][(d.getDay()+6)%7];
  const dayNum  = (d) => d.getDate();
  const today = new Date(); today.setHours(0,0,0,0);
  const isToday = (d) => new Date(d).setHours(0,0,0,0) === today.getTime();
  const timeHHMM = (iso) => (iso || "").slice(11, 16);

  return (
    <div className="space-y-4" data-testid="weekly-view">
      {visibleBranches.map((br) => (
        <Panel key={br.id}>
          <PanelHeader title={br.name} subtitle="Click any day to switch to daily view" />
          <ClassTickerBanners branchId={br.id} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-2" data-testid={`week-grid-${br.id}`}>
            {weekDates.map((d, idx) => {
              const iso = isoDate(d);
              const dayResult = weekDayResults.find((r) => r.date === iso);
              const sessions = (dayResult?.sessions || [])
                .filter((s) => s.branch_id === br.id)
                .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
              return (
                <button
                  key={iso}
                  onClick={() => onSelectDay(iso)}
                  data-testid={`week-day-${br.id}-${idx}`}
                  className={`text-left bg-zinc-950 border rounded-sm p-2 hover:border-volt/50 transition-colors ${isToday(d) ? "border-volt/70" : "border-zinc-900"}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`text-[10px] uppercase tracking-[0.16em] font-bold ${isToday(d) ? "text-volt" : "text-zinc-400"}`}>
                      {dayName(d)}
                    </div>
                    <div className={`text-lg font-black font-display ${isToday(d) ? "text-volt" : "text-zinc-200"}`}>
                      {dayNum(d)}
                    </div>
                  </div>
                  {sessions.length === 0 ? (
                    <div className="text-[10px] text-zinc-600 italic">—</div>
                  ) : (
                    <div className="space-y-1">
                      {sessions.map((s) => (
                        <div key={s.id} className="flex items-center gap-1.5 border-l-2 pl-1.5 py-0.5" style={{ borderLeftColor: TYPE_COLOR[s.session_type] || "#3f3f46" }}>
                          <div className="text-[10px] font-mono text-zinc-400 shrink-0">{timeHHMM(s.start_time)}</div>
                          <div className="text-[10px] font-semibold truncate" style={{ color: TYPE_COLOR[s.session_type] || "#a1a1aa" }}>
                            {s.session_type}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </Panel>
      ))}
    </div>
  );
}

function SessionCard({ s, coaches, onReassign }) {
  const color = TYPE_COLOR[s.session_type] || "#71717A";
  const startHM = (s.start_time || "").slice(11, 16);
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 hover:border-zinc-700 transition-colors" data-testid={`session-${s.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color }}>{s.session_type}</div>
        <div className="text-[10px] text-zinc-500 flex items-center gap-1"><Clock size={10} />{startHM} · {s.duration_min}m</div>
      </div>
      <div className="text-sm font-semibold truncate">{s.title}</div>
      <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1"><Users size={10} />Expects {s.expected_attendance}</div>
      <div className="mt-3">
        <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Coach</Label>
        <Select value={s.coach_id || "none"} onValueChange={(v) => onReassign(v === "none" ? null : v)}>
          <SelectTrigger className="mt-1 h-8 bg-zinc-900 border-zinc-800 rounded-sm text-xs" data-testid={`coach-select-${s.id}`}>
            <SelectValue placeholder="Unassigned" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Unassigned —</SelectItem>
            {coaches.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}


/* ============================================================
   BRANCH ADMIN — weekly template view + change requests
   ============================================================ */
function BranchAdminScheduler() {
  const { activeBranchId, user } = useAuth();
  const qc = useQueryClient();
  const branchId = activeBranchId || (user?.branch_ids || [])[0];
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSlot, setCreateSlot] = useState({ day_of_week: 0, start_time: "06:30" });

  const { data: templates = [] } = useQuery({
    queryKey: ["templates", branchId],
    enabled: !!branchId,
    queryFn: async () => (await apiClient.get("/session-templates", { params: { branch_id: branchId } })).data,
  });

  const { data: myRequests = [] } = useQuery({
    queryKey: ["my-change-requests", branchId],
    enabled: !!branchId,
    queryFn: async () => (await apiClient.get("/session-change-requests", { params: { branch_id: branchId } })).data,
  });

  const createRequest = useMutation({
    mutationFn: async (payload) => (await apiClient.post("/session-change-requests", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-change-requests"] });
      toast.success("Sent for Super Admin approval");
      setSelectedTemplate(null);
      setCreateOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  // Group templates by day_of_week
  const byDay = useMemo(() => {
    const m = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    for (const t of templates) m[t.day_of_week].push(t);
    for (const d of Object.keys(m)) m[d].sort((a, b) => a.start_time.localeCompare(b.start_time));
    return m;
  }, [templates]);

  const pendingCount = myRequests.filter((r) => r.status === "PENDING").length;

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="branch-admin-scheduler">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Coach Hub · Weekly Template</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Recurring Timetable</h1>
          <div className="text-zinc-500 text-sm mt-1">
            Define what runs every week. Coach assignment is set by Super Admin.
            <span className="ml-2 text-zinc-400">Edits go for Super Admin approval.</span>
          </div>
        </div>
        {pendingCount > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-sm px-3 py-2 text-xs uppercase tracking-[0.14em] font-semibold" data-testid="pending-badge">
            {pendingCount} pending your last change request{pendingCount > 1 ? "s" : ""}
          </div>
        )}
      </div>

      <Panel>
        <PanelHeader title="Weekly Template" subtitle="Mon → Sat · Click a slot to edit · Empty rail to add a new session" />
        {branchId && <ClassTickerBanners branchId={branchId} />}
        <div className="space-y-3" data-testid="weekly-grid">
          {[0, 1, 2, 3, 4, 5].map((dow) => (
            <div key={dow} className="border-b border-zinc-900 pb-3 last:border-0">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-bold">{DAY_NAMES[dow]}</div>
                <Button
                  size="sm" variant="outline"
                  className="h-7 rounded-sm text-[10px]"
                  data-testid={`add-slot-${dow}`}
                  onClick={() => { setCreateSlot({ day_of_week: dow, start_time: "06:30" }); setCreateOpen(true); }}
                >
                  <Plus size={12} className="mr-1" /> Add session
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                {byDay[dow].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTemplate(t)}
                    data-testid={`template-${t.id}`}
                    className="text-left bg-zinc-950 border border-zinc-900 hover:border-volt/50 rounded-sm p-3 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: TYPE_COLOR[t.session_type] || "#71717A" }}>
                        {t.session_type}
                      </div>
                      <div className="text-[10px] text-zinc-500 flex items-center gap-1"><Clock size={10} />{t.start_time} · {t.duration_min}m</div>
                    </div>
                    <div className="text-sm font-semibold mt-1 truncate">{t.title}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1">
                      <UserIcon size={10} /> {t.default_coach_name || "(unassigned — Super Admin)"}
                    </div>
                  </button>
                ))}
                {byDay[dow].length === 0 && (
                  <div className="text-[11px] text-zinc-600 italic">No sessions on {DAY_NAMES[dow]}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* My recent requests */}
      <Panel>
        <PanelHeader title="My Change Requests" subtitle="Recent proposals to Super Admin" />
        {myRequests.length === 0 ? (
          <div className="text-zinc-500 text-sm">No change requests filed yet.</div>
        ) : (
          <div className="space-y-2" data-testid="my-requests-list">
            {myRequests.slice(0, 10).map((r) => (
              <div key={r.id} className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 flex items-center justify-between" data-testid={`my-request-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.14em] text-zinc-500">
                    {r.action} {r.target_type} · {new Date(r.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="text-sm mt-0.5 truncate">{r.reason || "(no reason)"}</div>
                </div>
                <div className={`text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-sm ${
                  r.status === "APPROVED" ? "bg-volt/10 text-volt" :
                  r.status === "REJECTED" ? "bg-red-500/10 text-red-400" :
                  "bg-amber-500/10 text-amber-400"
                }`}>{r.status}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Edit / delete existing template — files a change request */}
      <TemplateEditDialog
        template={selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        onEditRequest={(payload, reason) => createRequest.mutate({
          branch_id: branchId, target_type: "TEMPLATE", target_id: selectedTemplate.id,
          action: "UPDATE", payload, reason,
        })}
        onDeleteRequest={(reason) => createRequest.mutate({
          branch_id: branchId, target_type: "TEMPLATE", target_id: selectedTemplate.id,
          action: "DELETE", payload: {}, reason,
        })}
      />

      {/* Create new template — files a change request */}
      <TemplateCreateDialog
        open={createOpen} onOpenChange={setCreateOpen}
        defaults={createSlot}
        onCreate={(payload, reason) => createRequest.mutate({
          branch_id: branchId, target_type: "TEMPLATE", target_id: null,
          action: "CREATE", payload, reason,
        })}
      />
    </div>
  );
}

function TemplateEditDialog({ template, onClose, onEditRequest, onDeleteRequest }) {
  const [start, setStart] = useState("");
  const [duration, setDuration] = useState(60);
  const [type, setType] = useState("OPEN_GYM");
  const [title, setTitle] = useState("");
  const [expected, setExpected] = useState(0);
  const [reason, setReason] = useState("");

  React.useEffect(() => {
    if (template) {
      setStart(template.start_time);
      setDuration(template.duration_min);
      setType(template.session_type);
      setTitle(template.title);
      setExpected(template.expected_attendance);
      setReason("");
    }
  }, [template]);

  if (!template) return null;

  return (
    <Dialog open={!!template} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-lg" data-testid="template-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">Edit {DAY_NAMES[template.day_of_week]} · {template.start_time}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">Changes go to Super Admin for approval before taking effect.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Row label="Start (HH:MM)"><Input value={start} onChange={(e) => setStart(e.target.value)} placeholder="06:30" className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="edit-start" /></Row>
          <Row label="Duration">
            <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="edit-duration"><SelectValue /></SelectTrigger>
              <SelectContent>{DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          <Row label="Type">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="edit-type"><SelectValue /></SelectTrigger>
              <SelectContent>{SESSION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          <Row label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="edit-title" /></Row>
          <Row label="Expected"><Input type="number" value={expected} onChange={(e) => setExpected(Number(e.target.value))} className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="edit-expected" /></Row>
          <Row label="Reason for change">
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why?" className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="edit-reason" />
          </Row>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="destructive" onClick={() => onDeleteRequest(reason || "Delete template")} data-testid="edit-request-delete" className="rounded-sm">
            <Trash2 size={14} className="mr-1" /> Request Delete
          </Button>
          <Button
            className="bg-volt hover:bg-volt-hover text-black rounded-sm"
            data-testid="edit-request-update"
            onClick={() => onEditRequest({
              start_time: start, duration_min: duration, session_type: type, title, expected_attendance: expected,
            }, reason || "Edit template")}
          >
            <Send size={14} className="mr-1" /> Send for Approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateCreateDialog({ open, onOpenChange, defaults, onCreate }) {
  const [start, setStart] = useState(defaults?.start_time || "06:30");
  const [duration, setDuration] = useState(60);
  const [type, setType] = useState("OPEN_GYM");
  const [title, setTitle] = useState("");
  const [expected, setExpected] = useState(12);
  const [reason, setReason] = useState("");

  React.useEffect(() => {
    if (open) {
      setStart(defaults?.start_time || "06:30");
      setDuration(60); setType("OPEN_GYM"); setTitle(""); setExpected(12); setReason(""); setArea("");
    }
  }, [open, defaults]);

  const [area, setArea] = useState("");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-lg" data-testid="template-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            New Session · {DAY_NAMES[defaults?.day_of_week ?? 0]}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">A new template proposal — Super Admin must approve before it goes live.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Row label="Start"><Input value={start} onChange={(e) => setStart(e.target.value)} placeholder="06:30" className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="create-start" /></Row>
          <Row label="Duration">
            <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="create-duration"><SelectValue /></SelectTrigger>
              <SelectContent>{DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          <Row label="Type">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="create-type"><SelectValue /></SelectTrigger>
              <SelectContent>{SESSION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          <Row label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Hyrox Block" className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="create-title" /></Row>
          <Row label="Area / Room">
            <Input value={area} onChange={(e) => setArea(e.target.value)} placeholder="e.g. Mat Area, Weights Room, Cardio Zone" className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="create-area" />
          </Row>
          <Row label="Expected"><Input type="number" value={expected} onChange={(e) => setExpected(Number(e.target.value))} className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="create-expected" /></Row>
          <Row label="Reason">
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why add this?" className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="create-reason" />
          </Row>
        </div>
        <DialogFooter>
          <Button
            className="bg-volt hover:bg-volt-hover text-black rounded-sm"
            data-testid="create-request-submit"
            onClick={() => onCreate({
              day_of_week: defaults.day_of_week,
              start_time: start, duration_min: duration, session_type: type,
              title: title || type.replace("_", " "), expected_attendance: expected,
            }, reason || "New session")}
          >
            <Send size={14} className="mr-1" /> Send for Approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}


/* ============================================================
   ROUTER — role-aware entry
   ============================================================ */
export default function SchedulePage() {
  const { user } = useAuth();
  if (user?.role === "SUPER_ADMIN") return <SuperAdminScheduler />;
  if (user?.role === "ADMIN") return <BranchAdminScheduler />;
  return <div className="p-8 text-zinc-500">Coaches see their sessions in the Coach Hub.</div>;
}
